/**
 * Phase 107 (Plan 107-08) — delete-band confirmation.
 *
 * Mirrors `RevokeConfirmModal.tsx`'s title/body/footer structure via the
 * shared `Modal` shell, but with **no** type-to-confirm input — deleting a
 * band is lower-stakes and far more frequent than an offboarding access
 * revoke (107-UI-SPEC.md). Names the band being deleted and, when it has
 * painted hours, states how many will be erased with it — matching what
 * the backend's cascading band delete actually does.
 */
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { SlaPricingBand } from '../../services/api/slaPricing';

export interface DeleteBandConfirmModalProps {
  isOpen: boolean;
  band: SlaPricingBand | null;
  paintedHours: number;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function DeleteBandConfirmModal({
  isOpen,
  band,
  paintedHours,
  onClose,
  onConfirm,
  isLoading = false,
}: DeleteBandConfirmModalProps) {
  if (!band) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Delete "${band.name}"?`}
      ariaLabelledBy="delete-band-modal-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isLoading}>
            Delete band
          </Button>
        </div>
      }
    >
      <p className="text-gray-600">
        {paintedHours > 0
          ? `${paintedHours} painted hour(s) using this band will be erased too.`
          : 'This band has no painted hours.'}
      </p>
    </Modal>
  );
}

export default DeleteBandConfirmModal;
